import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, ChevronLeft, ChevronRight, Heart, X, Star, Users, Video, ArrowLeft, Send, Sparkles, Package, CheckCircle, MessageCircle, ExternalLink, Loader2, ChevronDown } from 'lucide-react';
import authService from '../services/AuthService';

// ─── Tiny helpers ─────────────────────────────────────────────────────────────
import { API_BASE } from '../consts';
const API = (path) => `${API_BASE}/api/amazon${path}`;

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// ─── Shimmer skeleton ─────────────────────────────────────────────────────────
const Shimmer = ({ className = '' }) => (
    <div className={`relative overflow-hidden bg-gray-100 rounded-xl ${className}`}>
        <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
    </div>
);

// ─── Step indicator ───────────────────────────────────────────────────────────
const Steps = ({ current }) => {
    const steps = [
        { n: 1, label: 'Find Brand' },
        { n: 2, label: 'Pick Product' },
        { n: 3, label: 'Swipe Creators' },
        { n: 4, label: 'Your Picks' },
    ];
    return (
        <div className="flex items-center gap-0 mb-10">
            {steps.map((s, i) => (
                <React.Fragment key={s.n}>
                    <div className="flex flex-col items-center">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-500
              ${current >= s.n
                                ? 'bg-gradient-to-br from-teal-500 to-blue-600 text-white shadow-lg shadow-teal-200'
                                : 'bg-gray-100 text-gray-400'}`}>
                            {current > s.n ? <CheckCircle size={18} /> : s.n}
                        </div>
                        <span className={`text-xs mt-1 font-medium transition-colors duration-300 ${current >= s.n ? 'text-teal-600' : 'text-gray-400'}`}>
                            {s.label}
                        </span>
                    </div>
                    {i < steps.length - 1 && (
                        <div className={`flex-1 h-0.5 mb-5 mx-1 transition-all duration-700 ${current > s.n ? 'bg-gradient-to-r from-teal-500 to-blue-600' : 'bg-gray-200'}`} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );
};

// ─── Product card (grid) ──────────────────────────────────────────────────────
const ProductCard = ({ product, selected, onClick }) => (
    <div
        onClick={onClick}
        className={`relative cursor-pointer rounded-2xl border-2 transition-all duration-300 overflow-hidden group
      ${selected
                ? 'border-teal-500 shadow-xl shadow-teal-100 scale-[1.02]'
                : 'border-gray-100 hover:border-teal-300 hover:shadow-lg hover:scale-[1.01]'}
      bg-white`}
    >
        {selected && (
            <div className="absolute top-2 right-2 z-10 w-7 h-7 bg-teal-500 rounded-full flex items-center justify-center shadow-lg animate-bounceIn">
                <CheckCircle size={16} className="text-white" />
            </div>
        )}
        <div className="aspect-square bg-gray-50 overflow-hidden">
            {product.image
                ? <img src={product.image} alt={product.title} className="w-full h-full object-contain p-3 group-hover:scale-110 transition-transform duration-500" />
                : <div className="w-full h-full flex items-center justify-center"><Package size={40} className="text-gray-300" /></div>
            }
        </div>
        <div className="p-3">
            <p className="text-xs font-semibold text-teal-600 mb-0.5 uppercase tracking-wide truncate">{product.brand}</p>
            <p className="text-sm text-gray-800 font-medium leading-tight line-clamp-2">{product.title}</p>
            <p className="text-xs text-gray-400 mt-1 font-mono">{product.asin}</p>
        </div>
    </div>
);

// ─── Category autocomplete ───────────────────────────────────────────────────
const CategoryAutocomplete = ({ value, onSelect }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const debounceRef = useRef(null);

    const search = useCallback(async (term) => {
        if (term.trim().length < 3) { setResults([]); return; }
        setLoading(true);
        try {
            const r = await authService.authenticatedFetch(API(`/category-search?term=${encodeURIComponent(term)}`));
            const data = await r.json();
            setResults(data.categories || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);

    const handleChange = (e) => {
        const v = e.target.value;
        setQuery(v);
        setOpen(true);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => search(v), 350);
    };

    return (
        <div className="relative">
            <input
                value={value ? value.name : query}
                onChange={handleChange}
                onFocus={() => setOpen(true)}
                placeholder="e.g. Protein Powder, Dog Toys, Yoga Mats..."
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 transition"
            />
            {loading && <Loader2 size={16} className="animate-spin absolute right-4 top-3.5 text-gray-400" />}
            {open && results.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-72 overflow-y-auto">
                    {results.map(c => (
                        <button
                            key={c.categoryId}
                            onClick={() => { onSelect(c); setQuery(''); setOpen(false); }}
                            className="w-full text-left px-4 py-2.5 hover:bg-teal-50 transition flex items-center justify-between gap-3 border-b border-gray-50 last:border-0"
                        >
                            <span className="text-sm text-gray-800">{c.name}</span>
                            <span className="text-xs text-gray-400 font-mono shrink-0">{c.productCount?.toLocaleString()} products</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── Tinder-style swipe card ──────────────────────────────────────────────────
const SwipeCard = ({ item, onLike, onSkip, isTop }) => {
    const cardRef = useRef(null);
    const [drag, setDrag] = useState({ active: false, x: 0, y: 0, startX: 0, startY: 0 });
    const [decision, setDecision] = useState(null); // 'like' | 'skip'
    const [videoError, setVideoError] = useState(false);
    const videoRef = useRef(null);

    useEffect(() => {
        if (!videoRef.current) return;
        if (isTop) {
            videoRef.current.play().catch(() => { });
        } else {
            videoRef.current.pause();
        }
    }, [isTop]);

    const video = item.videos?.[0];
    const thumbName = video?.image;
    const thumbUrl = thumbName ? `https://m.media-amazon.com/images/I/${thumbName}` : null;

    const rotate = drag.x * 0.08;
    const opacity = Math.max(0, 1 - Math.abs(drag.x) / 400);

    const onPointerDown = (e) => {
        if (!isTop) return;
        cardRef.current?.setPointerCapture(e.pointerId);
        setDrag({ active: true, x: 0, y: 0, startX: e.clientX, startY: e.clientY });
    };

    const onPointerMove = (e) => {
        if (!drag.active) return;
        const dx = e.clientX - drag.startX;
        const dy = e.clientY - drag.startY;
        setDrag(d => ({ ...d, x: dx, y: dy }));
        if (dx > 60) setDecision('like');
        else if (dx < -60) setDecision('skip');
        else setDecision(null);
    };

    const onPointerUp = () => {
        if (!drag.active) return;
        setDrag(d => ({ ...d, active: false }));
        if (drag.x > 100) { onLike(); return; }
        if (drag.x < -100) { onSkip(); return; }
        setDrag({ active: false, x: 0, y: 0, startX: 0, startY: 0 });
        setDecision(null);
    };

    return (
        <div
            ref={cardRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            style={{
                transform: isTop
                    ? `translateX(${drag.x}px) translateY(${drag.y * 0.3}px) rotate(${rotate}deg)`
                    : `scale(0.95) translateY(12px)`,
                opacity: isTop ? opacity : 0.7,
                zIndex: isTop ? 10 : 5,
                transition: drag.active ? 'none' : 'all 0.35s cubic-bezier(.25,.8,.25,1)',
                cursor: isTop ? (drag.active ? 'grabbing' : 'grab') : 'default',
                touchAction: 'none',
                userSelect: 'none',
            }}
            className="absolute inset-0 bg-white rounded-3xl shadow-2xl overflow-hidden"
        >
            {/* Like / Skip overlays */}
            {isTop && (
                <>
                    <div className={`absolute top-6 left-6 z-20 px-4 py-2 bg-green-500 text-white font-black text-2xl rounded-xl border-4 border-green-400 rotate-[-12deg] transition-all duration-150 ${decision === 'like' ? 'opacity-100 scale-110' : 'opacity-0 scale-90'}`}>
                        SAVE ❤️
                    </div>
                    <div className={`absolute top-6 right-6 z-20 px-4 py-2 bg-red-500 text-white font-black text-2xl rounded-xl border-4 border-red-400 rotate-[12deg] transition-all duration-150 ${decision === 'skip' ? 'opacity-100 scale-110' : 'opacity-0 scale-90'}`}>
                        SKIP ✕
                    </div>
                </>
            )}

            {/* Video / thumbnail */}
            <div className="relative w-full h-[55%] bg-gray-900 overflow-hidden">
                {video?.url && !videoError ? (
                    <video
                        ref={videoRef}
                        key={video.url}
                        src={video.url}
                        className="w-full h-full object-contain"
                        loop playsInline
                        muted={!isTop}
                        preload="auto"
                        onError={() => setVideoError(true)}
                    />
                ) : thumbUrl ? (
                    <img src={thumbUrl} alt="thumb" className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <Video size={48} className="text-gray-600" />
                    </div>
                )}

                {/* Influencer badge */}
                <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-bold">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    INFLUENCER VIDEO
                </div>

                {/* Product thumbnail */}
                {item.image && (
                    <div className="absolute bottom-3 right-3 w-14 h-14 rounded-xl overflow-hidden border-2 border-white shadow-lg bg-white">
                        <img src={item.image} alt="product" className="w-full h-full object-contain p-1" />
                    </div>
                )}
            </div>

            {/* Info */}
            <div className="p-5 flex flex-col gap-2 h-[45%] overflow-hidden">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white font-bold text-sm">
                            {(video?.name || 'I')[0].toUpperCase()}
                        </div>
                        <div>
                            <p className="font-bold text-gray-900 text-sm leading-tight">{video?.name || 'Influencer'}</p>
                            <p className="text-xs text-teal-500 font-medium">Amazon Influencer</p>
                        </div>
                    </div>
                    <p className="text-xs text-gray-500 font-medium truncate">{video?.title || 'Product Review'}</p>
                </div>

                <div className="mt-auto">
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{item.brand}</p>
                    <p className="text-sm font-semibold text-gray-800 line-clamp-2 leading-snug">{item.title}</p>
                    <p className="text-xs text-gray-400 font-mono mt-1">{item.asin}</p>
                </div>

                {video?.duration > 0 && (
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Video size={11} />
                        <span>{Math.floor(video.duration / 60)}:{String(video.duration % 60).padStart(2, '0')}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Favorites panel ──────────────────────────────────────────────────────────
const FavoriteCard = ({ item, onDm }) => {
    const video = item.videos?.[0];
    const favVideoRef = useRef(null);

    const handleFindStorefront = () => {
        const searchUrl = `https://www.google.com/search?q=${encodeURIComponent((video?.name || '') + ' site:amazon.com/shop')}`;
        window.open(searchUrl, '_blank');
    };

    return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 animate-fadeIn">
            <div className="relative h-36 bg-gray-900 overflow-hidden">
                {video?.url ? (
                    <video
                        ref={favVideoRef}
                        src={video.url}
                        className="w-full h-full object-contain"
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                    />
                ) : item.image ? (
                    <img src={item.image} alt={item.title} className="w-full h-full object-contain p-2 bg-white" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-800">
                        <Video size={32} className="text-gray-600" />
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-2 left-3 text-white">
                    <p className="text-xs font-bold leading-tight">{video?.name || 'Influencer'}</p>
                    <p className="text-[10px] text-gray-300 truncate max-w-[140px]">{video?.title}</p>
                </div>
                <div className="absolute top-2 right-2 bg-red-500 rounded-full p-1">
                    <Heart size={12} className="text-white fill-white" />
                </div>
            </div>
            <div className="p-3">
                <p className="text-xs text-teal-600 font-semibold truncate">{item.brand}</p>
                <p className="text-sm font-medium text-gray-800 line-clamp-2 leading-tight mb-3">{item.title}</p>
                <div className="flex flex-col gap-2">
                    {video?.name && (
                        <button
                            onClick={handleFindStorefront}
                            className="w-full flex items-center justify-center gap-1.5 py-2 bg-gray-800 text-white text-sm font-semibold rounded-xl hover:bg-gray-700 transition"
                        >
                            <ExternalLink size={14} />
                            Find Socials
                        </button>
                    )}
                    <button
                        onClick={() => onDm(item)}
                        className="w-full flex items-center justify-center gap-1.5 py-2 bg-gradient-to-r from-teal-500 to-blue-600 text-white text-sm font-semibold rounded-xl hover:opacity-90 transition shadow"
                    >
                        <MessageCircle size={14} /> DM Influencer
                    </button>
                    <p className="text-[10px] text-center text-gray-400 leading-tight">
                        DM is an enterprise service. Use <span className="text-teal-500 font-semibold">Find Socials</span> to reach out directly.
                    </p>
                </div>
            </div>
        </div>
    );
};

// ─── DM Modal (demo) ──────────────────────────────────────────────────────────
const DmModal = ({ item, onClose }) => {
    const [msg, setMsg] = useState('');
    const [sent, setSent] = useState(false);
    const influencer = item?.videos?.[0]?.name || 'Influencer';

    const handleSend = () => {
        if (!msg.trim()) return;
        setSent(true);
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn" onClick={onClose}>
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="bg-gradient-to-r from-teal-500 to-blue-600 p-6 text-white">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold">
                            {influencer[0].toUpperCase()}
                        </div>
                        <div>
                            <p className="font-bold text-lg">{influencer}</p>
                            <p className="text-teal-100 text-sm">Amazon Influencer</p>
                        </div>
                    </div>
                </div>

                {sent ? (
                    <div className="p-8 text-center">
                        <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounceIn">
                            <MessageCircle size={32} className="text-teal-500" />
                        </div>
                        <p className="text-xl font-bold text-gray-800 mb-1">This is an Enterprise Service</p>
                        <p className="text-gray-500 text-sm mb-4">
                            DM outreach to influencers like <span className="font-semibold text-teal-600">{influencer}</span> is part of our full enterprise plan. Use <span className="font-semibold text-teal-500">Find Socials</span> to reach out directly for now.
                        </p>
                        <a
                            href="mailto:contact@multisellerpro.com"
                            className="block w-full py-2.5 mb-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-semibold hover:opacity-90 transition text-sm"
                        >
                            Contact Us for Access
                        </a>
                        <button onClick={onClose} className="w-full py-2.5 border border-gray-200 rounded-xl text-gray-500 font-semibold hover:bg-gray-50 transition text-sm">
                            Close
                        </button>
                    </div>
                ) : (
                    <div className="p-6">
                        <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 mb-4">
                            <p className="text-xs text-teal-700 font-semibold">💼 Enterprise Service</p>
                            <p className="text-xs text-teal-600">This is a preview of our full DM service. <a href="mailto:contact@multisellerpro.com" className="underline font-bold">Contact us</a> to get access.</p>
                        </div>
                        <p className="text-sm font-semibold text-gray-700 mb-2">Your message to {influencer}:</p>
                        <textarea
                            value={msg}
                            onChange={e => setMsg(e.target.value)}
                            placeholder={`Hi ${influencer}! I love your Amazon content and would love to collaborate on a product in your niche...`}
                            className="w-full h-32 border border-gray-200 rounded-xl p-3 text-sm text-gray-700 resize-none focus:outline-none focus:ring-2 focus:ring-teal-400 mb-4"
                        />
                        <div className="flex gap-3">
                            <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-600 font-semibold hover:bg-gray-50 transition text-sm">
                                Cancel
                            </button>
                            <button onClick={handleSend} disabled={!msg.trim()} className="flex-1 py-2.5 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-semibold hover:opacity-90 transition text-sm flex items-center justify-center gap-2 disabled:opacity-40">
                                <Send size={14} /> Send
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Floating particles background ───────────────────────────────────────────
const Particles = () => (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(8)].map((_, i) => (
            <div
                key={i}
                className="absolute rounded-full opacity-20 animate-float"
                style={{
                    width: `${40 + i * 20}px`,
                    height: `${40 + i * 20}px`,
                    left: `${10 + i * 12}%`,
                    top: `${5 + (i % 4) * 22}%`,
                    background: i % 2 === 0
                        ? 'radial-gradient(circle, #14b8a6, transparent)'
                        : 'radial-gradient(circle, #3b82f6, transparent)',
                    animationDelay: `${i * 0.7}s`,
                    animationDuration: `${4 + i * 0.5}s`,
                }}
            />
        ))}
    </div>
);

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const InfluencerHunterPage = ({ user }) => {
    const [step, setStep] = useState(1);

    // Search mode toggle
    const [searchMode, setSearchMode] = useState('brand'); // 'brand' | 'category'

    // Step 1 — brand search
    const [brandInput, setBrandInput] = useState('');
    const [asinInput, setAsinInput] = useState('');
    const [brandLoading, setBrandLoading] = useState(false);
    const [brandProducts, setBrandProducts] = useState([]);
    const [brandTotal, setBrandTotal] = useState(0);
    const [brandPage, setBrandPage] = useState(0);
    const [brandError, setBrandError] = useState('');

    // Step 1 — category search (alternate mode)
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categorySalesMin, setCategorySalesMin] = useState('200');
    const [categorySalesMax, setCategorySalesMax] = useState('5000');
    const [categoryLoading, setCategoryLoading] = useState(false);
    const [categoryError, setCategoryError] = useState('');

    // Step 2 — selected product
    const [selectedProduct, setSelectedProduct] = useState(null);

    // Step 3 — swipe
    const [swipeItems, setSwipeItems] = useState([]);
    const [swipeIdx, setSwipeIdx] = useState(0);
    const [swipeLoading, setSwipeLoading] = useState(false);
    const [swipeError, setSwipeError] = useState('');
    const [swipeStats, setSwipeStats] = useState(null);
    const [favorites, setFavorites] = useState([]);

    // Step 4 — dm modal
    const [dmTarget, setDmTarget] = useState(null);

    // ── fetch brand products ───────────────────────────────────────────────────
    const fetchBrandProducts = useCallback(async (brand, page = 0) => {
        setBrandLoading(true);
        setBrandError('');
        try {
            const r = await authService.authenticatedFetch(API(`/brand-products?brand=${encodeURIComponent(brand)}&page=${page}&per_page=10`));
            const data = await r.json();
            if (data.error) throw new Error(data.error);
            setBrandProducts(data.products || []);
            setBrandTotal(data.totalResults || 0);
            setBrandPage(page);
        } catch (e) {
            setBrandError(e.message || 'Failed to load products');
        } finally {
            setBrandLoading(false);
        }
    }, []);

    // ── fetch influencer videos for a category directly ────────────────────────
    const fetchSwipeItemsForCategory = useCallback(async (categoryId, salesMin, salesMax) => {
        setSwipeLoading(true);
        setSwipeError('');
        setSwipeItems([]);
        setSwipeIdx(0);
        try {
            const params = new URLSearchParams({
                category_id: categoryId,
                limit: '60',
            });
            if (salesMin) params.set('sales_min', salesMin);
            if (salesMax) params.set('sales_max', salesMax);

            const r = await authService.authenticatedFetch(API(`/influencer-videos-for-category?${params.toString()}`));
            const data = await r.json();
            if (data.error && !data.videos?.length) throw new Error(data.error);
            setSwipeItems(data.videos || []);
            setSwipeStats({ total: data.totalScanned, withVideos: data.withVideos });
        } catch (e) {
            setSwipeError(e.message || 'Failed to load influencer videos');
        } finally {
            setSwipeLoading(false);
        }
    }, []);

    // ── fetch influencer videos for selected ASIN ──────────────────────────────
    const fetchSwipeItems = useCallback(async (asin, brand) => {
        setSwipeLoading(true);
        setSwipeError('');
        setSwipeItems([]);
        setSwipeIdx(0);
        try {
            const r = await authService.authenticatedFetch(API(`/influencer-videos-for-asin/${asin}?exclude_brand=${encodeURIComponent(brand || '')}&limit=60`));
            const data = await r.json();
            if (data.error && !data.videos?.length) throw new Error(data.error);
            setSwipeItems(data.videos || []);
            setSwipeStats({ total: data.totalCompetitors, withVideos: data.withVideos });
        } catch (e) {
            setSwipeError(e.message || 'Failed to load influencer videos');
        } finally {
            setSwipeLoading(false);
        }
    }, []);

    // ── handlers ───────────────────────────────────────────────────────────────
    const handleBrandSearch = () => {
        if (!brandInput.trim()) return;
        fetchBrandProducts(brandInput.trim(), 0);
    };

    const handleCategorySearch = () => {
        if (!selectedCategory) return;
        setSelectedProduct({
            asin: null,
            title: selectedCategory.name,
            brand: '',
            image: null,
            isCategory: true,
        });
        setStep(3);
        fetchSwipeItemsForCategory(selectedCategory.categoryId, categorySalesMin, categorySalesMax);
    };

    const handleSkipToAsin = () => {
        if (!asinInput.trim()) return;
        const fake = { asin: asinInput.trim(), title: asinInput.trim(), brand: '', image: null };
        setSelectedProduct(fake);
        setStep(3);
        fetchSwipeItems(asinInput.trim(), '');
    };

    const handleSelectProduct = (p) => {
        setSelectedProduct(p);
        setStep(3);
        fetchSwipeItems(p.asin, p.brand || '');
    };

    const handleLike = () => {
        if (swipeIdx < swipeItems.length) {
            setFavorites(prev => {
                const already = prev.find(f => f.asin === swipeItems[swipeIdx].asin);
                return already ? prev : [...prev, swipeItems[swipeIdx]];
            });
        }
        setSwipeIdx(i => i + 1);
    };

    const handleSkip = () => setSwipeIdx(i => i + 1);

    const currentItem = swipeItems[swipeIdx];
    const nextItem = swipeItems[swipeIdx + 1];
    const swipeDone = swipeIdx >= swipeItems.length && !swipeLoading;
    const totalPages = Math.ceil(brandTotal / 10);

    // ── render ─────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pb-16 relative">
            <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounceIn {
          0%   { transform: scale(0); }
          60%  { transform: scale(1.15); }
          100% { transform: scale(1); }
        }
        @keyframes shimmer {
          100% { transform: translateX(200%); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) scale(1); }
          50%       { transform: translateY(-20px) scale(1.05); }
        }
        @keyframes pulse-ring {
          0%   { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(20,184,166,.4); }
          70%  { transform: scale(1);    box-shadow: 0 0 0 14px rgba(20,184,166,0); }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(20,184,166,0); }
        }
        @keyframes swipeLeft {
          to { transform: translateX(-130%) rotate(-20deg); opacity: 0; }
        }
        @keyframes swipeRight {
          to { transform: translateX(130%) rotate(20deg); opacity: 0; }
        }
        .animate-fadeIn    { animation: fadeIn .35s ease both; }
        .animate-bounceIn  { animation: bounceIn .4s cubic-bezier(.25,.8,.25,1) both; }
        .animate-float     { animation: float 4s ease-in-out infinite; }
        .animate-pulse-ring{ animation: pulse-ring 2s cubic-bezier(.455,.03,.515,.955) infinite; }
        .line-clamp-2 { display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
      `}</style>

            <Particles />

            <div className="max-w-5xl mx-auto px-4 pt-10 relative z-10">
                {/* Header */}
                <div className="mb-8 animate-fadeIn">
                    <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-blue-600 flex items-center justify-center shadow-lg">
                            <Sparkles className="text-white" size={20} />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Influencer Hunter</h1>
                            <p className="text-sm text-gray-500">Find Amazon influencers reviewing products in your niche</p>
                        </div>
                    </div>
                </div>

                <Steps current={step} />

                {/* ── STEP 1: Brand or Category search ─────────────────────────────── */}
                {step === 1 && (
                    <div className="animate-fadeIn">
                        {/* Mode toggle */}
                        <div className="flex gap-2 mb-5 bg-gray-100 p-1.5 rounded-2xl w-fit">
                            <button
                                onClick={() => setSearchMode('brand')}
                                className={`px-5 py-2 rounded-xl text-sm font-semibold transition ${searchMode === 'brand' ? 'bg-white text-teal-600 shadow' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                By Brand
                            </button>
                            <button
                                onClick={() => setSearchMode('category')}
                                className={`px-5 py-2 rounded-xl text-sm font-semibold transition ${searchMode === 'category' ? 'bg-white text-teal-600 shadow' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                By Category
                            </button>
                        </div>

                        {searchMode === 'category' && (
                            <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100 mb-6">
                                <h2 className="text-xl font-bold text-gray-800 mb-1 flex items-center gap-2">
                                    <Search size={20} className="text-teal-500" />
                                    Search by Category
                                </h2>
                                <p className="text-gray-500 text-sm mb-6">Pick a category and sales range — we'll scan products in it for influencer videos directly, no need to pick a single product first.</p>

                                <div className="grid md:grid-cols-3 gap-4 mb-5">
                                    <div className="md:col-span-2">
                                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Category</label>
                                        <CategoryAutocomplete value={selectedCategory} onSelect={setSelectedCategory} />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Monthly sold range</label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                value={categorySalesMin}
                                                onChange={e => setCategorySalesMin(e.target.value.replace(/\D/g, ''))}
                                                className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm text-center focus:outline-none focus:ring-2 focus:ring-teal-400"
                                            />
                                            <span className="text-gray-300">–</span>
                                            <input
                                                value={categorySalesMax}
                                                onChange={e => setCategorySalesMax(e.target.value.replace(/\D/g, ''))}
                                                className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm text-center focus:outline-none focus:ring-2 focus:ring-teal-400"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {selectedCategory && (
                                    <p className="text-xs text-gray-400 mb-4">Selected: <span className="font-semibold text-teal-600">{selectedCategory.name}</span></p>
                                )}

                                {categoryError && <p className="text-sm text-red-500 mb-4">⚠️ {categoryError}</p>}

                                <button
                                    onClick={handleCategorySearch}
                                    disabled={!selectedCategory || categoryLoading}
                                    className="px-6 py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition shadow-lg disabled:opacity-40 flex items-center gap-2"
                                >
                                    {categoryLoading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                                    Find Influencers in This Category
                                </button>
                            </div>
                        )}

                        {searchMode === 'brand' && (
                            <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100 mb-6">
                                <h2 className="text-xl font-bold text-gray-800 mb-1 flex items-center gap-2">
                                    <Search size={20} className="text-teal-500" />
                                    Search by Brand
                                </h2>
                                <p className="text-gray-500 text-sm mb-6">Enter a brand name to browse their Amazon products, then pick one to find competitor influencers.</p>

                                <div className="flex gap-3">
                                    <input
                                        value={brandInput}
                                        onChange={e => setBrandInput(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleBrandSearch()}
                                        placeholder="e.g. Callaway, TaylorMade, Nike Golf..."
                                        className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 transition"
                                    />
                                    <button
                                        onClick={handleBrandSearch}
                                        disabled={brandLoading || !brandInput.trim()}
                                        className="px-6 py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition shadow-lg disabled:opacity-40 flex items-center gap-2"
                                    >
                                        {brandLoading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                                        Search
                                    </button>
                                </div>

                                {/* OR skip with ASIN */}
                                <div className="mt-5 pt-5 border-t border-dashed border-gray-200">
                                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-3">— or jump straight with an ASIN —</p>
                                    <div className="flex gap-3">
                                        <input
                                            value={asinInput}
                                            onChange={e => setAsinInput(e.target.value)}
                                            placeholder="e.g. B08N5WRWNW"
                                            className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition font-mono"
                                        />
                                        <button
                                            onClick={handleSkipToAsin}
                                            disabled={!asinInput.trim()}
                                            className="px-6 py-3 bg-gray-800 text-white rounded-xl font-semibold text-sm hover:bg-gray-700 transition disabled:opacity-40"
                                        >
                                            Go →
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Results — brand mode only */}
                        {searchMode === 'brand' && brandError && (
                            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-red-700 text-sm animate-fadeIn mb-4">
                                ⚠️ {brandError}
                            </div>
                        )}

                        {searchMode === 'brand' && brandLoading && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 animate-fadeIn">
                                {[...Array(10)].map((_, i) => (
                                    <div key={i} className="rounded-2xl overflow-hidden border border-gray-100" style={{ animationDelay: `${i * 0.05}s` }}>
                                        <Shimmer className="aspect-square rounded-none" />
                                        <div className="p-3 space-y-2">
                                            <Shimmer className="h-3 rounded-md w-2/3" />
                                            <Shimmer className="h-3 rounded-md w-full" />
                                            <Shimmer className="h-3 rounded-md w-4/5" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {searchMode === 'brand' && !brandLoading && brandProducts.length > 0 && (
                            <>
                                <div className="flex items-center justify-between mb-4">
                                    <p className="text-sm text-gray-500">
                                        Showing <span className="font-semibold text-gray-800">{brandProducts.length}</span> of{' '}
                                        <span className="font-semibold text-gray-800">{brandTotal.toLocaleString()}</span> products for{' '}
                                        <span className="font-semibold text-teal-600">"{brandInput}"</span>
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => fetchBrandProducts(brandInput, brandPage - 1)}
                                            disabled={brandPage === 0 || brandLoading}
                                            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30 transition"
                                        >
                                            <ChevronLeft size={16} />
                                        </button>
                                        <span className="text-xs text-gray-500 font-medium">{brandPage + 1} / {totalPages || 1}</span>
                                        <button
                                            onClick={() => fetchBrandProducts(brandInput, brandPage + 1)}
                                            disabled={brandPage >= totalPages - 1 || brandLoading}
                                            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30 transition"
                                        >
                                            <ChevronRight size={16} />
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                                    {brandProducts.map((p, i) => (
                                        <div key={p.asin} className="animate-fadeIn" style={{ animationDelay: `${i * 0.05}s` }}>
                                            <ProductCard
                                                product={p}
                                                selected={selectedProduct?.asin === p.asin}
                                                onClick={() => handleSelectProduct(p)}
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-6 text-center">
                                    <p className="text-sm text-gray-400">Click any product to find influencers reviewing similar items →</p>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* ── STEP 3: Swipe ────────────────────────────────────────────────── */}
                {step === 3 && (
                    <div className="animate-fadeIn">
                        {/* Back + product context */}
                        <div className="flex items-center gap-3 mb-6">
                            <button
                                onClick={() => { setStep(1); setSwipeItems([]); setSwipeIdx(0); }}
                                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition font-medium"
                            >
                                <ArrowLeft size={16} /> Back
                            </button>
                            {selectedProduct && (
                                <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full shadow border border-gray-100 ml-2">
                                    {selectedProduct.image && (
                                        <img src={selectedProduct.image} alt="" className="w-6 h-6 object-contain rounded" />
                                    )}
                                    <span className="text-xs font-semibold text-gray-700 truncate max-w-[180px]">{selectedProduct.title}</span>
                                </div>
                            )}
                            {favorites.length > 0 && (
                                <button
                                    onClick={() => setStep(4)}
                                    className="ml-auto flex items-center gap-1.5 bg-red-50 border border-red-200 text-red-600 px-4 py-1.5 rounded-full text-sm font-semibold hover:bg-red-100 transition animate-pulse-ring"
                                >
                                    <Heart size={14} className="fill-red-500 text-red-500" />
                                    {favorites.length} saved
                                </button>
                            )}
                        </div>

                        {/* Loading state */}
                        {swipeLoading && (
                            <div className="flex flex-col items-center justify-center py-24 gap-6">
                                <div className="relative">
                                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-teal-500 to-blue-600 animate-pulse flex items-center justify-center shadow-xl">
                                        <Video size={36} className="text-white" />
                                    </div>
                                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
                                        <Loader2 size={18} className="text-teal-500 animate-spin" />
                                    </div>
                                </div>
                                <div className="text-center">
                                    <p className="text-lg font-bold text-gray-800 mb-1">Hunting Influencers…</p>
                                    <p className="text-sm text-gray-500">Scanning {selectedProduct?.asin} competitor products for creator videos</p>
                                </div>
                                <div className="flex gap-2">
                                    {[0, 1, 2].map(i => (
                                        <div key={i} className="w-2 h-2 rounded-full bg-teal-500 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Error */}
                        {swipeError && !swipeLoading && (
                            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center animate-fadeIn">
                                <p className="text-red-600 font-semibold mb-2">⚠️ {swipeError}</p>
                                <button onClick={() => fetchSwipeItems(selectedProduct?.asin, selectedProduct?.brand)} className="text-sm text-teal-600 underline">Try again</button>
                            </div>
                        )}

                        {/* Stats bar */}
                        {!swipeLoading && swipeStats && swipeItems.length > 0 && (
                            <div className="flex items-center gap-4 mb-6 animate-fadeIn">
                                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-100 text-xs text-gray-600">
                                    <Package size={13} className="text-teal-500" /> {swipeStats.total} competitors found
                                </div>
                                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-100 text-xs text-gray-600">
                                    <Video size={13} className="text-blue-500" /> {swipeStats.withVideos} with influencer videos
                                </div>
                                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-100 text-xs text-gray-600">
                                    <Heart size={13} className="text-red-400" /> {favorites.length} saved
                                </div>
                                {swipeItems.length > 0 && (
                                    <div className="ml-auto text-xs text-gray-400">
                                        {Math.min(swipeIdx + 1, swipeItems.length)} / {swipeItems.length}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Swipe area */}
                        {!swipeLoading && !swipeError && swipeItems.length > 0 && !swipeDone && (
                            <div className="flex flex-col items-center gap-6">
                                {/* Card stack */}
                                <div className="relative w-full max-w-sm h-[540px] mx-auto">
                                    {nextItem && <SwipeCard key={swipeIdx + 1} item={nextItem} onLike={() => { }} onSkip={() => { }} isTop={false} />}
                                    {currentItem && <SwipeCard key={swipeIdx} item={currentItem} onLike={handleLike} onSkip={handleSkip} isTop={true} />}
                                </div>

                                {/* Action buttons */}
                                <div className="flex items-center gap-8">
                                    <button
                                        onClick={handleSkip}
                                        className="w-16 h-16 rounded-full bg-white shadow-xl border-2 border-gray-200 flex items-center justify-center text-gray-400 hover:text-red-500 hover:border-red-300 hover:shadow-red-100 transition-all duration-200 hover:scale-110 active:scale-95"
                                    >
                                        <X size={28} />
                                    </button>
                                    <div className="text-center">
                                        <p className="text-xs text-gray-400">Swipe or tap</p>
                                        <p className="text-xs text-gray-300">drag cards to decide</p>
                                    </div>
                                    <button
                                        onClick={handleLike}
                                        className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-blue-600 shadow-xl flex items-center justify-center text-white hover:shadow-teal-200 transition-all duration-200 hover:scale-110 active:scale-95"
                                    >
                                        <Heart size={28} className="fill-white" />
                                    </button>
                                </div>

                                <p className="text-xs text-gray-400">← Skip &nbsp;&nbsp;|&nbsp;&nbsp; Save →</p>
                            </div>
                        )}

                        {/* Done state */}
                        {!swipeLoading && swipeDone && (
                            <div className="flex flex-col items-center justify-center py-20 gap-5 animate-fadeIn">
                                <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-teal-500 to-blue-600 flex items-center justify-center shadow-2xl animate-bounceIn">
                                    <CheckCircle size={48} className="text-white" />
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-gray-900 mb-1">All done!</p>
                                    <p className="text-gray-500">You reviewed {swipeItems.length} influencer videos</p>
                                    {favorites.length > 0 && (
                                        <p className="text-teal-600 font-semibold mt-1">You saved {favorites.length} creators ❤️</p>
                                    )}
                                </div>
                                <div className="flex gap-3">
                                    <button onClick={() => setStep(1)} className="px-5 py-2.5 border border-gray-200 rounded-xl text-gray-700 font-semibold text-sm hover:bg-gray-50 transition">
                                        ← Start over
                                    </button>
                                    {favorites.length > 0 && (
                                        <button onClick={() => setStep(4)} className="px-5 py-2.5 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition shadow-lg flex items-center gap-2">
                                            <Heart size={14} className="fill-white" /> View {favorites.length} saved
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* No videos found */}
                        {!swipeLoading && !swipeError && swipeItems.length === 0 && !swipeDone && (
                            <div className="flex flex-col items-center justify-center py-20 gap-4 animate-fadeIn">
                                <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center">
                                    <Video size={40} className="text-gray-300" />
                                </div>
                                <p className="text-lg font-bold text-gray-800">No influencer videos found</p>
                                <p className="text-sm text-gray-500 text-center max-w-xs">No competing products with influencer videos were found for this ASIN. Try a different product.</p>
                                <button onClick={() => setStep(1)} className="px-5 py-2.5 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition shadow">
                                    ← Try another
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* ── STEP 4: Favorites ────────────────────────────────────────────── */}
                {step === 4 && (
                    <div className="animate-fadeIn">
                        <div className="flex items-center gap-3 mb-6">
                            <button
                                onClick={() => setStep(swipeDone ? 3 : 3)}
                                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition font-medium"
                            >
                                <ArrowLeft size={16} /> Back
                            </button>
                            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <Heart size={20} className="text-red-500 fill-red-500" />
                                Saved Creators
                                <span className="ml-1 bg-red-100 text-red-600 text-sm font-bold px-2 py-0.5 rounded-full">{favorites.length}</span>
                            </h2>
                        </div>

                        {favorites.length === 0 ? (
                            <div className="text-center py-20">
                                <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <Heart size={40} className="text-gray-300" />
                                </div>
                                <p className="text-gray-500">No saved creators yet.</p>
                                <button onClick={() => setStep(3)} className="mt-4 px-5 py-2 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition shadow">
                                    ← Go back and swipe
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="bg-teal-50 border border-teal-200 rounded-2xl p-4 mb-6 flex items-start gap-3">
                                    <span className="text-teal-500 text-lg">💼</span>
                                    <div>
                                        <p className="text-sm font-bold text-teal-800">DM - Enterprise Service</p>
                                        <p className="text-xs text-teal-600 mt-0.5">This is a preview of our full enterprise influencer outreach service. <a href="mailto:contact@multisellerpro.com" className="underline font-bold">Contact us</a> for more info.</p>
                                    </div>
                                </div>

                                <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                                    {favorites.map((fav, i) => (
                                        <div key={fav.asin} style={{ animationDelay: `${i * 0.07}s` }}>
                                            <FavoriteCard item={fav} onDm={(item) => setDmTarget(item)} />
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-8 flex items-center justify-between">
                                    <button onClick={() => setStep(3)} className="text-sm text-teal-600 font-semibold hover:underline flex items-center gap-1">
                                        <ArrowLeft size={14} /> Keep swiping
                                    </button>
                                    <button
                                        onClick={() => {
                                            alert(`This is a preview of our DM enterprise service. Contact us to send outreach to all ${favorites.length} influencers at once.`);
                                        }}
                                        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition shadow-lg"
                                    >
                                        <Send size={15} /> DM All {favorites.length} Creators
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* DM modal */}
            {dmTarget && <DmModal item={dmTarget} onClose={() => setDmTarget(null)} />}
        </div>
    );
};

export default InfluencerHunterPage;